import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PRIORITY_LABELS } from "@/lib/labels";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList,
  Plus,
  Pencil,
  Trash2,
  Power,
  ArrowUp,
  ArrowDown,
  History,
} from "lucide-react";

export const ANSWER_TYPES = [
  ["sim_nao_na", "Conforme / Não Conforme / N.A."],
  ["sim_nao", "Sim / Não / N.A."],
  ["selecao", "Seleção única"],
  ["selecao_multipla", "Seleção múltipla"],
  ["texto", "Texto"],
  ["numero", "Número"],
  ["quantidade", "Quantidade"],
];

const EVIDENCE_OPTIONS = [
  ["nao_conforme", "Não conforme"],
  ["conforme", "Conforme"],
  ["nao_aplicavel", "N.A."],
];

const EMPTY_FORM = {
  label: "",
  key: "",
  answer_type: "sim_nao_na",
  required: true,
  photo_required: false,
  evidence_required_when: [],
  is_nonconformity: true,
  severity: "media",
  active: true,
};

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

/**
 * CONFIGURAÇÃO DE CHECKLIST — PGA.
 *
 * Reaproveita a MESMA arquitetura administrativa já existente em Salas
 * Elétricas (adicionar / editar / ativar / desativar / reordenar), com as
 * particularidades do PGA:
 *
 *   - o checklist é POR TIPO DE ESTRUTURA (Silo, Armazém, Moega...);
 *   - tipos de resposta completos (conforme/NC/N.A., seleção, texto...);
 *   - `evidence_required_when` (ex.: Não Conforme -> foto obrigatória);
 *   - VERSIONAMENTO automático: toda mudança publica uma nova versão e o
 *     histórico já executado continua imutável.
 */
export default function PgaChecklistConfigDialog({
  open,
  onOpenChange,
  structureTypes,
  onSaved,
}) {
  const [typeId, setTypeId] = useState("");
  const [items, setItems] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    if (!typeId) return;

    setLoading(true);

    try {
      const [listRes, verRes] = await Promise.all([
        api.get("/pga/checklist-items", {
          params: { type_id: typeId, include_inactive: true },
        }),
        api.get("/pga/checklist-versions", { params: { type_id: typeId } }),
      ]);

      setItems(Array.isArray(listRes.data) ? listRes.data : []);
      setVersions(Array.isArray(verRes.data) ? verRes.data : []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Não foi possível carregar o checklist."
      );
    } finally {
      setLoading(false);
    }
  }, [typeId]);

  useEffect(() => {
    if (open && !typeId && structureTypes?.length) {
      setTypeId(structureTypes[0].id);
    }
  }, [open, structureTypes, typeId]);

  useEffect(() => {
    if (open) {
      load();
      setEditing(null);
      setShowForm(false);
      setConfirmDelete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, typeId]);

  if (!open) return null;

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setShowForm(true);
  };

  const startEdit = (item) => {
    setForm({
      label: item.label || "",
      key: item.key || "",
      answer_type: item.answer_type || "sim_nao_na",
      required: item.required !== false,
      photo_required: !!item.photo_required,
      evidence_required_when: item.evidence_required_when || [],
      is_nonconformity: item.is_nonconformity !== false,
      severity: item.severity || "media",
      active: item.active !== false,
    });
    setEditing(item);
    setShowForm(true);
  };

  const saveItem = async () => {
    if (!form.label?.trim()) return toast.error("Informe o texto da pergunta.");

    const payload = {
      label: form.label.trim(),
      key: form.key?.trim() || slugify(form.label),
      answer_type: form.answer_type,
      required: form.required,
      photo_required: form.photo_required,
      evidence_required_when: form.evidence_required_when,
      is_nonconformity: form.is_nonconformity,
      severity: form.severity,
      active: form.active,
    };

    setSaving(true);

    try {
      const res = editing?.id
        ? await api.put(`/pga/checklist-items/${editing.id}`, payload)
        : await api.post(`/pga/checklist-items?type_id=${typeId}`, payload);

      toast.success(
        `${editing?.id ? "Pergunta atualizada" : "Pergunta adicionada"} (checklist v${res.data?.template_version}).`
      );
      setShowForm(false);
      setEditing(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível salvar a pergunta.");
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = async (item) => {
    try {
      const res = await api.patch(`/pga/checklist-items/${item.id}/toggle`);
      toast.success(
        `${res.data.active ? "Ativada" : "Desativada"} — histórico preservado (v${res.data.template_version}).`
      );
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível alterar a situação.");
    }
  };

  const removeItem = async () => {
    if (!confirmDelete?.id) return;
    setSaving(true);

    try {
      const res = await api.delete(`/pga/checklist-items/${confirmDelete.id}`);
      toast.success(res.data.soft_deleted ? res.data.detail : "Pergunta removida (não havia histórico).");
      setConfirmDelete(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível remover a pergunta.");
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, direction) => {
    const next = [...items];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;

    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);

    try {
      const res = await api.post("/pga/checklist-items/reorder", {
        type_id: typeId,
        ids: next.map((i) => i.id),
      });
      toast.success(`Ordem atualizada (checklist v${res.data?.template_version}).`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível reordenar.");
      await load();
    }
  };

  const activeCount = items.filter((i) => i.active !== false).length;
  const currentVersion = versions.find((v) => v.active)?.version;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        data-testid="pga-checklist-config"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardList size={17} /> Configuração do checklist (PGA)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo de estrutura</Label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger className="w-56" data-testid="pga-checklist-type">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {(structureTypes || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {currentVersion ? (
              <Badge variant="outline" data-testid="pga-checklist-version">
                checklist v{currentVersion}
              </Badge>
            ) : null}
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando checklist...</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {activeCount} pergunta(s) ativa(s) de {items.length}
                </p>

                {!showForm && (
                  <Button size="sm" onClick={resetForm} data-testid="pga-checklist-add">
                    <Plus size={14} className="mr-1.5" /> Adicionar pergunta
                  </Button>
                )}
              </div>

              <PgaChecklistForm
                showForm={showForm}
                editing={editing}
                form={form}
                setForm={setForm}
                saving={saving}
                onSave={saveItem}
                onCancel={() => { setShowForm(false); setEditing(null); }}
              />

              <div className="space-y-1.5">
                {items.map((item, index) => (
                  <PgaChecklistRow
                    key={item.id}
                    item={item}
                    index={index}
                    total={items.length}
                    onMove={move}
                    onEdit={startEdit}
                    onToggle={toggleItem}
                    onDelete={setConfirmDelete}
                  />
                ))}

                {items.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    Nenhuma pergunta cadastrada neste tipo de estrutura.
                  </p>
                )}
              </div>

              {confirmDelete && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 space-y-2">
                  <p className="text-sm">Remover <strong>{confirmDelete.label}</strong>?</p>
                  <p className="text-xs text-muted-foreground">
                    Se a pergunta já foi usada em monitoramentos, ela será apenas
                    desativada para preservar o histórico.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="destructive" onClick={removeItem} disabled={saving}>
                      Confirmar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {versions.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <History size={12} /> Versões do checklist
                  </p>

                  <div className="flex items-center gap-2 flex-wrap">
                    {versions.map((v) => (
                      <Badge
                        key={v.version}
                        variant="outline"
                        className={v.active ? "border-primary text-primary" : ""}
                        title={`${v.items_count} pergunta(s) · ${v.created_at}`}
                      >
                        v{v.version} ({v.items_count})
                      </Badge>
                    ))}
                  </div>

                  <p className="text-[11px] text-muted-foreground">
                    Alterações futuras publicam uma nova versão. Monitoramentos já
                    executados continuam com a pergunta original.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PgaChecklistForm({ showForm, editing, form, setForm, saving, onSave, onCancel }) {
  if (!showForm) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
      <p className="text-xs font-semibold">
        {editing?.id ? "Editar pergunta" : "Nova pergunta"}
      </p>

      <div className="space-y-1.5">
        <Label className="text-xs">Pergunta</Label>
        <Input
          value={form.label}
          onChange={(e) => setForm((c) => ({ ...c, label: e.target.value }))}
          placeholder="Ex.: Vedação e integridade estrutural"
          data-testid="pga-checklist-label"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Tipo de resposta</Label>
          <Select
            value={form.answer_type}
            onValueChange={(v) => setForm((c) => ({ ...c, answer_type: v }))}
          >
            <SelectTrigger data-testid="pga-checklist-answer-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANSWER_TYPES.map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Criticidade</Label>
          <Select
            value={form.severity}
            onValueChange={(v) => setForm((c) => ({ ...c, severity: v }))}
          >
            <SelectTrigger data-testid="pga-checklist-severity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Evidência obrigatória quando...</Label>
        <div className="flex items-center gap-3 flex-wrap">
          {EVIDENCE_OPTIONS.map(([v, l]) => (
            <label key={v} className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={form.evidence_required_when.includes(v)}
                onChange={(e) =>
                  setForm((c) => ({
                    ...c,
                    evidence_required_when: e.target.checked
                      ? [...c.evidence_required_when, v]
                      : c.evidence_required_when.filter((x) => x !== v),
                  }))
                }
              />
              {l}
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" className="h-3.5 w-3.5" checked={form.required}
            onChange={(e) => setForm((c) => ({ ...c, required: e.target.checked }))} />
          Obrigatória
        </label>

        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" className="h-3.5 w-3.5" checked={form.photo_required}
            onChange={(e) => setForm((c) => ({ ...c, photo_required: e.target.checked }))} />
          Foto sempre obrigatória
        </label>

        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" className="h-3.5 w-3.5" checked={form.is_nonconformity}
            onChange={(e) => setForm((c) => ({ ...c, is_nonconformity: e.target.checked }))} />
          Pode ser não conformidade
        </label>

        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" className="h-3.5 w-3.5" checked={form.active}
            onChange={(e) => setForm((c) => ({ ...c, active: e.target.checked }))} />
          Ativa
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="pga-checklist-save">
          Salvar
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

function PgaChecklistRow({ item, index, total, onMove, onEdit, onToggle, onDelete }) {
  return (
    <div
      className="rounded-md border border-border px-3 py-2 flex items-center justify-between gap-2"
      data-testid={`pga-checklist-item-${item.id}`}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">
          {item.label}
          {item.active === false && (
            <span className="text-[10px] text-muted-foreground ml-2">desativada</span>
          )}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {ANSWER_TYPES.find(([v]) => v === item.answer_type)?.[1] || item.answer_type}
          {item.required ? " · obrigatória" : ""}
          {item.photo_required ? " · foto obrigatória" : ""}
          {item.evidence_required_when?.length
            ? ` · evidência quando: ${item.evidence_required_when.join(", ")}`
            : ""}
        </p>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Badge variant="outline" className="text-[10px]">
          {PRIORITY_LABELS[item.severity] || item.severity}
        </Badge>

        <Button variant="ghost" size="icon" className="h-7 w-7"
          onClick={() => onMove(index, -1)} disabled={index === 0} title="Subir">
          <ArrowUp size={13} />
        </Button>

        <Button variant="ghost" size="icon" className="h-7 w-7"
          onClick={() => onMove(index, 1)} disabled={index === total - 1} title="Descer">
          <ArrowDown size={13} />
        </Button>

        <Button variant="ghost" size="icon" className="h-7 w-7"
          onClick={() => onEdit(item)} title="Editar"
          data-testid={`pga-checklist-edit-${item.id}`}>
          <Pencil size={13} />
        </Button>

        <Button variant="ghost" size="icon" className="h-7 w-7"
          onClick={() => onToggle(item)}
          title={item.active === false ? "Ativar" : "Desativar"}
          data-testid={`pga-checklist-toggle-${item.id}`}>
          <Power size={13} />
        </Button>

        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
          onClick={() => onDelete(item)} title="Remover"
          data-testid={`pga-checklist-delete-${item.id}`}>
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  );
}
